import * as React from 'react';

import { isEqual, keyBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { Grid, Spacer, Typography } from '@emburse/ui-core';

import { ICardsGatewayIssuingModalValues } from '@Actions/cardsGatewayIssuingModalActions';
import { getExpenseBucketsForUser } from '@Actions/expenseBucketsWithTagsActions';
import { IAppStateContainer } from '@Reducers/index';

import { ICompanyCategoryModel, IExpenseTagBucketModel } from '@Shared/models';

import { CategorySelector } from '../Fields/CategorySelector';
import { ExpenseTagBucketsFormControls } from '../Fields/ExpenseTagBucketsFormControls';

interface IExpenseFieldsFormProps {
  cardsIssuingExpenseFieldsFeatureFlag: boolean;

  assigneeUserId?: string;
  assigneeEmail?: string;
  selectedCategoryId?: string;
  selectedExpenseTagsByBucketId: Record<string, string>;
  textExpenseTagsByBucketId: Record<string, string>;

  activeExpenseBucketsWithActiveTags: IExpenseTagBucketModel[];
  disabled: boolean;
  onChange: (update: Partial<ICardsGatewayIssuingModalValues>) => void;
}

// Check if selected tag bucket is available for selected user
// If if is not available then remove if from the state
const getUpdatedBucketSelections = (
  expenseTagsBucketsForUser: IExpenseTagBucketModel[],
  selectedExpenseTagsByBucketId: Record<string, string>,
  textExpenseTagsByBucketId: Record<string, string>
) => {
  const bucketsById = keyBy(expenseTagsBucketsForUser, 'id');

  const updatedDropdownSelections = Object.entries(selectedExpenseTagsByBucketId).reduce((selections, [key, value]) => {
    return bucketsById[key] ? { ...selections, [key]: value } : selections;
  }, {});

  const updatedInputSelections = Object.entries(textExpenseTagsByBucketId).reduce((selections, [key, value]) => {
    return bucketsById[key] ? { ...selections, [key]: value } : selections;
  }, {});

  if (!isEqual(selectedExpenseTagsByBucketId, updatedDropdownSelections) || !isEqual(textExpenseTagsByBucketId, updatedInputSelections)) {
    return {
      selectedExpenseTagsByBucketId: updatedDropdownSelections,
      textExpenseTagsByBucketId: updatedInputSelections
    };
  }
};

export const ExpenseFieldsFormBase: React.FC<IExpenseFieldsFormProps> = ({
  cardsIssuingExpenseFieldsFeatureFlag,

  assigneeUserId,
  assigneeEmail,
  selectedCategoryId,
  selectedExpenseTagsByBucketId,
  textExpenseTagsByBucketId,

  activeExpenseBucketsWithActiveTags,
  disabled,
  onChange
}) => {
  const hasNoUserSelected = window.FEATURES.accelerated_card_issuing ? !assigneeUserId && !assigneeEmail : !assigneeUserId;
  const shouldNotRender = !cardsIssuingExpenseFieldsFeatureFlag || hasNoUserSelected;

  const { expenseBucketsByUser, expenseBucketsWithTagsLoading } = useSelector((state: IAppStateContainer) => state.expenseBucketsWithTags);
  const { categoriesByUser, loading: categoriesLoading } = useSelector((state: IAppStateContainer) => state.categories);
  const loading = expenseBucketsWithTagsLoading || categoriesLoading;

  const dispatch = useDispatch();

  React.useEffect(() => {
    if ((!shouldNotRender && !expenseBucketsByUser[assigneeUserId]) || !categoriesByUser[assigneeUserId]) {
      dispatch(getExpenseBucketsForUser(assigneeUserId, Object.values(selectedExpenseTagsByBucketId)));
    }
  }, [assigneeUserId, shouldNotRender]);

  React.useEffect(() => {
    if (!shouldNotRender && expenseBucketsByUser[assigneeUserId]) {
      const updatedBucketSelections = getUpdatedBucketSelections(
        expenseBucketsByUser[assigneeUserId],
        selectedExpenseTagsByBucketId,
        textExpenseTagsByBucketId
      );
      if (updatedBucketSelections) {
        onChange(updatedBucketSelections);
      }
    }
  }, [expenseBucketsByUser, shouldNotRender]);

  // Whenever we finish loading, remember the previous assignee ID
  // The assigneeUserId prop changes right when you select a new assignee, but the buckets/categories in the store aren't updated until an async fetch is complete
  // Therefore, there is a brief period in between where we have a new assigneeUserId, but expenseBucketsByUser[assigneeUserId] is not loaded yet
  // During this brief period, keep showing the buckets/categories of the previousAssigneeId -- this prevents the fields from constantly disappearing and reappearing
  const [previousAssigneeId, setPreviousAssigneeId] = React.useState(null);
  React.useEffect(() => {
    if (!shouldNotRender && !loading) {
      setPreviousAssigneeId(assigneeUserId);
    }
  }, [loading, shouldNotRender]);

  if (shouldNotRender && !disabled) {
    return null;
  }

  const shouldUsePreviousAssigneeId = (loading && previousAssigneeId) || disabled;
  const assigneeIdToUse = shouldUsePreviousAssigneeId ? previousAssigneeId : assigneeUserId;

  if ((loading && !previousAssigneeId) || !expenseBucketsByUser[assigneeIdToUse] || !categoriesByUser[assigneeIdToUse]) {
    return null;
  }

  const categoryOptions = categoriesByUser[assigneeIdToUse];
  const expenseTagsBucketsForUser = expenseBucketsByUser[assigneeIdToUse];

  const handleExpenseTagChange = (bucketId: string, val: string): void => {
    const bucket = expenseTagsBucketsForUser.find((etb) => etb.id === bucketId);
    let selections, updatedData;

    if (bucket.input_type === 'text') {
      selections = { ...textExpenseTagsByBucketId };
      selections[bucketId] = val;
      updatedData = { textExpenseTagsByBucketId: selections };
    } else {
      selections = { ...selectedExpenseTagsByBucketId };
      selections[bucketId] = val;
      updatedData = { selectedExpenseTagsByBucketId: selections };
    }
    if (!val) {
      delete selections[bucketId];
    }
    onChange(updatedData);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Spacer size={14} />
        <Typography variant="subtitle1" className={disabled ? 'disabled' : ''}>
          Additional Company Attributes
        </Typography>
      </Grid>
      <Grid className="category-picker" item xs={12}>
        <CategorySelector
          loading={loading}
          categories={categoryOptions}
          selectedCategoryId={selectedCategoryId}
          onCategorySelect={(category: ICompanyCategoryModel) => onChange({ selectedCategoryId: category?.id })}
          disabled={disabled}
        />
      </Grid>
      <ExpenseTagBucketsFormControls
        expenseTagsBucketsForUser={expenseTagsBucketsForUser}
        selectedExpenseTagsByBucketId={selectedExpenseTagsByBucketId}
        textExpenseTagsByBucketId={textExpenseTagsByBucketId}
        activeExpenseBucketsWithActiveTags={activeExpenseBucketsWithActiveTags}
        onExpenseTagChange={handleExpenseTagChange}
        disabled={disabled}
      />
    </Grid>
  );
};

export const ExpenseFieldsForm = React.memo(ExpenseFieldsFormBase);
